import { Controller } from "@hotwired/stimulus"
import { areaStore } from "../stores/locales/area_store"
import { allDevicesStore } from "../stores/devices/all_devices_store"
import { devicesLoadingStore } from "../stores/devices/devices_loading_store"
import { devicesBackgroundLoadStore } from "../stores/devices/devices_background_load_store"
import { useStore } from "stimulus-store"
import { sendFailureNotification } from "shared/toast_notifications"
import { getDevicesInLocale } from "shared/devices/device_discovery"

// Connects to data-controller="device-manager"
export default class extends Controller {

    static stores = [
        areaStore,
        allDevicesStore,
        devicesBackgroundLoadStore,
        devicesLoadingStore
    ]

    static values = {
        autoLoad: Boolean,
        itemType: String,

        // See region_select_controller.js for the full rationale: set only by pages that need
        // organization-wide (not active-project) results, e.g. the Reports page. Left unset,
        // this controller's behavior is unchanged (manage_devices/load.html.erb omits it).
        projectScopeToken: String
    }

    connect() {
        useStore(this)

        // allDevicesStore starts as [] (empty array)
        // devicesLoadingStore tracks whether devices are being loaded
        // This allows other controllers to distinguish between:
        // - loading = false, devices = []: not yet loaded
        // - loading = true, devices = []: currently loadings
        // - loading = false, devices = []: loaded but organization has no devices
        // - loading = false, devices = [...]: loaded with devices

        // If autoLoad is enabled, load devices.
        // Pass areaStoreValue (which may be null/undefined for manage devices page)
        //
        // Defer the fetch to the next microtask so sibling controllers on the
        // same element (notably argus--table, which owns the loading overlay)
        // finish connecting first. loadDevices() flips devicesLoadingStore to
        // true synchronously; if that happened during connect() — before
        // argus--table connected — items_table_controller's
        // onDevicesLoadingStoreUpdate(true) would run while _getArgusTable()
        // is still null, so showLoading() would be missed and the overlay
        // would never appear (only the later hideLoading() on completion would
        // land). Deferring guarantees the sibling is present when the loading
        // signal is emitted. This mirrors the same ordering workaround used in
        // items_table_controller#connect.
        if (this.autoLoadValue) {
            Promise.resolve().then(() => {
                // The initial autoLoad is a silent, greedy background prefetch.
                // Mark it so the store-driven loading overlay stays hidden — the
                // user hasn't asked for data yet (they interact via Apply / area
                // selection), so flashing a spinner on page load is confusing.
                this.loadDevices(this.areaStoreValue, { background: true })
            })
        }
    }

    // allDevicesStore is a module-level singleton that persists across Turbo
    // Drive navigations. Clear it here so a later page (or a subclass like
    // AreaDeviceManagerController) doesn't render stale devices left over from
    // this page before its own load completes. See region_select_controller.js
    // for the full rationale behind clearing on disconnect rather than on the
    // next page's connect.
    disconnect() {
        this.resetAllDevicesStore()
    }

    /**
     * Load all devices for the specified area
     * When area is null/undefined, this fetches all devices for the organization
     */
    async loadDevices(area, { background = false } = {}) {
        try {
            // Mark whether this load is a silent background prefetch (initial
            // autoLoad) or a user-initiated load. Consumers of the shared
            // loading overlay use this to stay silent during the prefetch while
            // still showing the overlay for Apply / area-selection loads.
            this.setDevicesBackgroundLoadStoreValue(background);

            // Set loading state to true
            this.setDevicesLoadingStoreValue(true);

            // Fetch devices using the area's location hierarchy
            // When area is null/undefined, this fetches all devices for the organization
            const devices = await getDevicesInLocale(
                this.itemTypeValue,
                area?.region,
                area?.building,
                area?.id,
                null, // room_id - null to get all devices in the area
                this.projectScopeTokenValue
            );

            // IMPORTANT: Always set to an array (even if empty) to indicate load complete
            // This distinguishes "loaded with no devices" from "not yet loaded"
            // - devices = [] means organization has no devices (valid state)
            this.setAllDevicesStoreValue(devices || []);
        } catch (error) {
            // Log detailed error information with context
            console.error('Error loading devices:', {
                error: error.message,
                errorStack: error.stack,
                area: area ? {
                    id: area.id,
                    name: area.name,
                    region: area.region,
                    building: area.building
                } : null,
                itemType: this.itemTypeValue,
                timestamp: new Date().toISOString()
            });

            // Show user-friendly error message
            const message = area?.name
                ? `Unable to load devices for ${ area.name }. Please try again.`
                : 'Unable to load devices. Please try again.';
            sendFailureNotification(message);

            // Set to empty array to indicate load complete with error
            // This prevents infinite waiting in the Item Filters Controller
            this.setAllDevicesStoreValue([]);
        } finally {
            // Set loading state to false when done (success or error)
            this.setDevicesLoadingStoreValue(false);

            // Clear the background-prefetch marker so any subsequent
            // user-initiated load surfaces the overlay normally.
            this.setDevicesBackgroundLoadStoreValue(false);
        }
    }

    /**
     * Manually refresh devices for the current area
     *
     * This method is called when devices need to be reloaded without changing the area.
     * It works with the updated loadDevicesForArea() logic:
     * - If an area is selected (has an id), it reloads devices for that area
     * - If no area is selected, it does nothing (no devices to refresh)
     *
     * The loading state is managed by loadDevicesForArea():
     * - Sets devicesLoadingStore to true while loading
     * - Sets allDevicesStore to [] if fetch returns no devices
     * - Sets allDevicesStore to [...] if fetch returns devices
     * - Sets devicesLoadingStore to false when complete
     */
    refreshDevices() {
        if (this.areaStoreValue?.id) {
            this.loadDevices(this.areaStoreValue);
        }
    }
}